import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Download, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Chip } from '@/ui/primitives';
import {
  TANARU_AMORTECIMENTO,
  TANARU_META,
  TANARU_PARQUE,
  type TanaruPerimetro,
} from '@/modules/fundiario/tanaru';
import { TANARU_MUNICIPIOS, TANARU_TIS } from '@/modules/fundiario/tanaruCamadas';

const TILE_LAYERS = {
  satelite: {
    label: 'Satélite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution:
      'Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, USDA, USGS, AeroGRID, IGN, GIS User Community',
    maxZoom: 19,
  },
  osm: {
    label: 'Mapa',
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19,
  },
} as const;

type TileKey = keyof typeof TILE_LAYERS;

// Cores das camadas do mapa (valores dinâmicos de canvas Leaflet, como em
// imoveis_.$id_.solo.tsx — não passam por CSS)
const COR_PARQUE = '#10b981';
const COR_AMORTECIMENTO = '#f59e0b';
const COR_TI = '#8b5cf6';
const COR_MUNICIPIO = '#64748b';

// Tooltip de label sem a caixa branca padrão do Leaflet
const LABEL_CLASS =
  '!bg-transparent !border-0 !shadow-none !p-0 text-[0.68rem] font-bold uppercase tracking-wide';

const fmtHa = (v: number) =>
  `${v.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ha`;

function popupHtml(titulo: string, areaHa: number, pontos: number, extra: string) {
  return `<strong>${titulo}</strong><br>Área: ${fmtHa(areaHa)}<br>Memorial: ${pontos} pontos<br><span style="opacity:.75">${extra}</span>`;
}

// KML usa cor aabbggrr (alpha, blue, green, red) — inverso do CSS rrggbb
function kmlColor(hex: string, alpha: string) {
  const r = hex.slice(1, 3);
  const g = hex.slice(3, 5);
  const b = hex.slice(5, 7);
  return `${alpha}${b}${g}${r}`;
}

function kmlPlacemark(nome: string, descricao: string, styleId: string, p: TanaruPerimetro) {
  const coords = p.geometry.coordinates[0]!
    .map(([lng, lat]) => `${lng},${lat},0`)
    .join(' ');
  return `    <Placemark>
      <name>${nome}</name>
      <description><![CDATA[${descricao}]]></description>
      <styleUrl>#${styleId}</styleUrl>
      <Polygon>
        <tessellate>1</tessellate>
        <outerBoundaryIs><LinearRing><coordinates>${coords}</coordinates></LinearRing></outerBoundaryIs>
      </Polygon>
    </Placemark>`;
}

function buildKml() {
  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${TANARU_META.nome}</name>
    <description><![CDATA[${TANARU_META.decreto} — DOU ${TANARU_META.dou}. Municípios: ${TANARU_META.municipios.join(', ')}/${TANARU_META.uf}. Memorial descritivo em ${TANARU_META.datum}, convertido pra WGS84. ${TANARU_META.url}]]></description>
    <Style id="parque">
      <LineStyle><color>${kmlColor(COR_PARQUE, 'ff')}</color><width>2.5</width></LineStyle>
      <PolyStyle><color>${kmlColor(COR_PARQUE, '59')}</color></PolyStyle>
    </Style>
    <Style id="amortecimento">
      <LineStyle><color>${kmlColor(COR_AMORTECIMENTO, 'ff')}</color><width>2</width></LineStyle>
      <PolyStyle><color>${kmlColor(COR_AMORTECIMENTO, '26')}</color></PolyStyle>
    </Style>
${kmlPlacemark(
  TANARU_META.nome,
  `Art. 2º — área de ${fmtHa(TANARU_PARQUE.areaHa)} (${TANARU_PARQUE.pontos} pontos). Posse e domínio públicos.`,
  'parque',
  TANARU_PARQUE,
)}
${kmlPlacemark(
  'Zona de amortecimento',
  `Art. 3º — área de ${fmtHa(TANARU_AMORTECIMENTO.areaHa)} (${TANARU_AMORTECIMENTO.pontos} pontos).`,
  'amortecimento',
  TANARU_AMORTECIMENTO,
)}
  </Document>
</kml>
`;
}

function downloadKml() {
  const blob = new Blob([buildKml()], {
    type: 'application/vnd.google-earth.kml+xml',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'parque-nacional-rio-tanaru.kml';
  a.click();
  URL.revokeObjectURL(url);
}

export function TanaruMapa() {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const tileLayerRef = useRef<L.TileLayer | null>(null);
  const municipiosRef = useRef<L.LayerGroup | null>(null);
  const tisRef = useRef<L.LayerGroup | null>(null);
  const perimetrosRef = useRef<L.GeoJSON[]>([]);
  const [tile, setTile] = useState<TileKey>('satelite');
  const [showMunicipios, setShowMunicipios] = useState(true);
  const [showTis, setShowTis] = useState(true);

  // Init do mapa + polígonos (uma vez)
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true });
    mapRef.current = map;

    // Camada de municípios — só divisas, com label no centro
    const municipios = L.layerGroup();
    for (const m of TANARU_MUNICIPIOS) {
      const layer = L.geoJSON(m.geometry as GeoJSON.GeoJsonObject, {
        style: { color: COR_MUNICIPIO, weight: 1.5, dashArray: '2 5', fill: false },
      });
      layer.bindTooltip(
        `<span style="color:${COR_MUNICIPIO};text-shadow:0 0 4px #fff,0 0 2px #fff">${m.nome}</span>`,
        { permanent: true, direction: 'center', className: LABEL_CLASS },
      );
      layer.addTo(municipios);
    }
    municipiosRef.current = municipios;

    // Camada de Terras Indígenas próximas (FUNAI, raio de 60 km)
    const tis = L.layerGroup();
    for (const t of TANARU_TIS) {
      const layer = L.geoJSON(t.geometry as GeoJSON.GeoJsonObject, {
        style: { color: COR_TI, weight: 2, fillColor: COR_TI, fillOpacity: 0.14 },
      });
      layer.bindPopup(
        `<strong>TI ${t.nome}</strong><br>Etnia: ${t.etnia}<br>Fase: ${t.fase}<br><span style="opacity:.75">Fonte: FUNAI (base do Hub)</span>`,
      );
      layer.bindTooltip(
        `<span style="color:${COR_TI};text-shadow:0 0 4px #fff,0 0 2px #fff">TI ${t.nome}</span>`,
        { permanent: true, direction: 'center', className: LABEL_CLASS },
      );
      layer.addTo(tis);
    }
    tisRef.current = tis;

    const amortecimento = L.geoJSON(TANARU_AMORTECIMENTO.geometry as GeoJSON.GeoJsonObject, {
      style: {
        color: COR_AMORTECIMENTO,
        weight: 2,
        dashArray: '6 4',
        fillColor: COR_AMORTECIMENTO,
        fillOpacity: 0.08,
      },
    })
      .bindPopup(
        popupHtml(
          'Zona de amortecimento',
          TANARU_AMORTECIMENTO.areaHa,
          TANARU_AMORTECIMENTO.pontos,
          'Art. 3º — pesquisa e lavra mineral admitidas mediante condicionantes do órgão gestor.',
        ),
      )
      .addTo(map);

    const parque = L.geoJSON(TANARU_PARQUE.geometry as GeoJSON.GeoJsonObject, {
      style: {
        color: COR_PARQUE,
        weight: 2.5,
        fillColor: COR_PARQUE,
        fillOpacity: 0.22,
      },
    })
      .bindPopup(
        popupHtml(
          TANARU_META.nome,
          TANARU_PARQUE.areaHa,
          TANARU_PARQUE.pontos,
          'Art. 2º — posse e domínio públicos; áreas particulares serão desapropriadas (art. 4º).',
        ),
      )
      .addTo(map);

    perimetrosRef.current = [amortecimento, parque];

    map.fitBounds(amortecimento.getBounds(), { padding: [24, 24] });

    return () => {
      map.remove();
      mapRef.current = null;
      municipiosRef.current = null;
      tisRef.current = null;
      perimetrosRef.current = [];
    };
  }, []);

  // Toggle das camadas de contexto — perímetros do decreto sempre por cima
  useEffect(() => {
    const map = mapRef.current;
    const municipios = municipiosRef.current;
    const tis = tisRef.current;
    if (!map) return;
    if (municipios) {
      if (showMunicipios) municipios.addTo(map);
      else municipios.remove();
    }
    if (tis) {
      if (showTis) tis.addTo(map);
      else tis.remove();
    }
    for (const p of perimetrosRef.current) p.bringToFront();
  }, [showMunicipios, showTis]);

  // Troca de tile layer
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const cfg = TILE_LAYERS[tile];
    if (tileLayerRef.current) tileLayerRef.current.remove();
    tileLayerRef.current = L.tileLayer(cfg.url, {
      attribution: cfg.attribution,
      maxZoom: cfg.maxZoom,
    }).addTo(map);
    tileLayerRef.current.bringToBack();
  }, [tile]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Chip tone="ok" dot>
          Parque: {fmtHa(TANARU_PARQUE.areaHa)}
        </Chip>
        <Chip tone="warn" dot>
          Zona de amortecimento: {fmtHa(TANARU_AMORTECIMENTO.areaHa)}
        </Chip>
        <Chip tone="neutral">
          {TANARU_META.municipios.join(', ')} / {TANARU_META.uf}
        </Chip>
        <Chip tone="neutral">{TANARU_META.datum}</Chip>
        <a
          href={TANARU_META.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-[0.78rem] text-text-2 hover:text-accent ml-auto"
        >
          <ExternalLink size={13} strokeWidth={1.5} />
          Decreto no Planalto
        </a>
        <button
          type="button"
          onClick={downloadKml}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[0.78rem]',
            'border border-border bg-elevated text-text-2 transition-colors',
            'hover:bg-surface2 hover:text-text',
          )}
        >
          <Download size={13} strokeWidth={1.5} />
          Baixar KML
        </button>
        <div className="flex items-center gap-1">
          {(Object.keys(TILE_LAYERS) as TileKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTile(k)}
              className={cn(
                'px-3 h-7 rounded-full text-[0.78rem] border border-border transition-colors',
                tile === k
                  ? 'bg-[rgba(217,119,87,0.1)] text-accent border-transparent'
                  : 'bg-elevated text-text-2 hover:bg-surface2 hover:text-text',
              )}
            >
              {TILE_LAYERS[k].label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowMunicipios((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[0.78rem] border transition-colors',
              showMunicipios
                ? 'border-border bg-surface2 text-text'
                : 'border-border bg-elevated text-text-muted hover:text-text',
            )}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: COR_MUNICIPIO, opacity: showMunicipios ? 1 : 0.35 }}
            />
            Municípios
          </button>
          <button
            type="button"
            onClick={() => setShowTis((v) => !v)}
            className={cn(
              'inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-[0.78rem] border transition-colors',
              showTis
                ? 'border-border bg-surface2 text-text'
                : 'border-border bg-elevated text-text-muted hover:text-text',
            )}
          >
            <span
              className="inline-block w-2 h-2 rounded-full"
              style={{ background: COR_TI, opacity: showTis ? 1 : 0.35 }}
            />
            Terras Indígenas
          </button>
        </div>
      </div>

      <div className="rounded-xl border border-border overflow-hidden">
        <div ref={containerRef} className="h-[calc(100vh-300px)] min-h-[420px]" />
      </div>

      <p className="text-[0.78rem] text-text-muted">
        Perímetros do memorial descritivo do {TANARU_META.decreto} (DOU {TANARU_META.dou}),
        convertidos de UTM Zona 20 Sul (SIRGAS 2000) pra WGS84. Clique nos polígonos pra ver os
        detalhes de cada limite.
      </p>
    </div>
  );
}
